import React, { useState, useEffect, useContext, useMemo } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../context/LanguageContext";
import { FaFileInvoice, FaPlus } from "react-icons/fa";
import { DataGridTable, SearchBar, FilterDateGroup, FiltersContainer } from "../components";
import { UserContext } from "../context/UserContext";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import "../styles/common.css";
import "./Factures.css";

export default function Factures() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // États pour les filtres
  const [fournisseur, setFournisseur] = useState('');
  const [dateAchat, setDateAchat] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // État pour le zoom (gardé pour les colonnes qui utilisent zoomLevel dans leurs styles)
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('factures-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  useEffect(() => {
    localStorage.setItem('factures-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  useEffect(() => {
    const fetchFactures = async () => {
      try {
        const params = new URLSearchParams();
        if (fournisseur) params.append('fournisseur', fournisseur);
        if (dateAchat) params.append('date_achat', dateAchat);
        if (searchTerm) params.append('search', searchTerm);

        const response = await api.get("/api/factures/", { params });
        console.log("Factures reçues de l'API:", response.data);
        if (response.data.length > 0) {
          console.log("Première facture (exemple):", response.data[0]);
          console.log("Nombre de produits dans la première facture:", response.data[0].nombre_produits);
          
          // Vérifier si on peut récupérer les achats pour cette facture
          const firstFactureId = response.data[0].id;
          try {
            const achatsResponse = await api.get(`/api/achats-produits/?facture=${firstFactureId}`);
            console.log(`Achats pour la facture ${firstFactureId}:`, achatsResponse.data);
            console.log(`Nombre d'achats trouvés: ${achatsResponse.data.length}`);
          } catch (achatsErr) {
            console.error("Erreur lors de la récupération des achats:", achatsErr);
          }
        }
        setFactures(response.data);
        setLoading(false);
      } catch (err) {
        setError("Impossible de charger les factures.");
        setLoading(false);
      }
    };
    fetchFactures();
  }, [fournisseur, dateAchat, searchTerm]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "0 FCFA";
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(amount).replace('XOF', 'FCFA');
  };


  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "numero",
        headerName: "Numéro",
        flex: 1,
        minWidth: 150,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: "fournisseur",
        headerName: "Fournisseur",
        flex: 1,
        minWidth: 200,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: "date_achat",
        headerName: "Date d'achat",
        width: 150,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => formatDate(value),
      },
      {
        field: "montant_total",
        headerName: "Montant total",
        width: 150,
        headerAlign: 'right',
        align: 'right',
        valueGetter: (value) => formatCurrency(value),
      },
      {
        field: "nombre_produits",
        headerName: "Nb produits",
        width: 120,
        headerAlign: 'center',
        align: 'center',
      },
    ];

    const detailsColumn = [
      {
        field: "details",
        headerName: "Détails",
        width: 130,
        sortable: false,
        filterable: false,
        headerAlign: 'center',
        align: 'center',
            renderCell: (params) => (
              <button
                onClick={() => navigate(`/factures/${params.row.id}`)}
                className="grid-action-button"
                style={{ fontSize: `${1.3 * zoomLevel}rem` }}
              >
                Voir plus
              </button>
            ),
      },
    ];

    const canEdit = user?.is_admin || hasPermission(user, PERMISSIONS.CHANGE_FACTURE);
    
    const adminColumns = canEdit
      ? [
          {
            field: "edit",
            headerName: t.actions,
            width: 130,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
              <button
                onClick={() => navigate(`/factures/${params.row.id}/modifier`)}
                className="grid-action-button"
                style={{ fontSize: `${1.3 * zoomLevel}rem` }}
              >
                {t.edit}
              </button>
            ),
          },
        ]
      : [];

    return [...baseColumns, ...detailsColumn, ...adminColumns];
  }, [user, navigate, zoomLevel, t]);

  return (
    <div className="factures-page">
      <div className="title-badge">
        <FaFileInvoice className="page-title-icon" aria-hidden="true" />
        <span>{t.invoiceManagement}</span>
      </div>
      {(user?.is_admin || hasPermission(user, PERMISSIONS.ADD_FACTURE)) && (
        <div className="action-buttons-container">
          <button
            type="button"
            className="save-but"
            onClick={() => navigate("/ajouter-facture")}
          >
            <FaPlus />
            <span>{t.addInvoice}</span>
          </button>
        </div>
      )}
      {loading && <p>{t.loading}</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && (
        <div className="factures-container">
          <div className="top-section">
            <SearchBar
              placeholder={t.searchByInvoice}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="compact"
            />
          </div>
          <FiltersContainer title={t.filters}>
            <input
              type="text"
              className="filtre-fournisseur filter-select"
              placeholder={t.supplier}
              value={fournisseur}
              onChange={(e) => setFournisseur(e.target.value)}
              style={{ width: '200px' }}
            />
            <FilterDateGroup
              label={t.purchaseDate}
              name="dateAchat"
              value={dateAchat}
              onChange={(e) => setDateAchat(e.target.value)}
            />
          </FiltersContainer>

          {/* DataGrid */}
          <DataGridTable
            rows={factures}
            columns={columns}
            loading={loading}
            storageKey="factures-zoom-level"
            zoomLevel={zoomLevel}
            onZoomChange={setZoomLevel}
            initialSort={{ field: 'date_achat', sort: 'desc' }}
            pageSize={25}
            containerClassName="factures-grid-container"
          />
        </div>
      )}
    </div>
  );
}